import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import api from '../services/api';

const ProjectContext = createContext(null);

export function ProjectProvider({ children }) {
  const [project, setProject] = useState(null);
  const [modules, setModules] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadProject = useCallback(async (projectId) => {
    if (project?.id === projectId) return project;

    setLoading(true);
    try {
      const [projRes, membersRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/members`),
      ]);
      const proj = projRes.data.data;
      setProject(proj);
      setMembers(membersRes.data.data || []);
      setModules(proj.enabled_modules || []);
      return proj;
    } catch (err) {
      setProject(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  const clearProject = useCallback(() => {
    setProject(null);
    setModules([]);
    setMembers([]);
  }, []);

  const isInPhase = useCallback((phase) => {
    if (!project) return false;
    if (Array.isArray(phase)) return phase.includes(project.phase);
    return project.phase === phase;
  }, [project]);

  const isModuleEnabled = useCallback((moduleSlug) => {
    return modules.some(m => m.slug === moduleSlug && m.is_enabled);
  }, [modules]);

  const value = useMemo(() => ({
    project,
    modules,
    members,
    loading,
    loadProject,
    clearProject,
    isInPhase,
    isModuleEnabled,
  }), [project, modules, members, loading, loadProject, clearProject, isInPhase, isModuleEnabled]);

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  const context = useContext(ProjectContext);
  if (!context) {
    // Return safe defaults when used outside provider (e.g., during initial render)
    return {
      project: null,
      modules: [],
      members: [],
      loading: false,
      loadProject: () => Promise.resolve(null),
      clearProject: () => {},
      isInPhase: () => false,
      isModuleEnabled: () => false,
    };
  }
  return context;
}

export default ProjectContext;
